import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { User } from 'firebase/auth';
import firebaseAuthService, { UserProfile } from '../services/firebase/FirebaseAuthService';
import { auth } from '../config/firebase';
import { UserRole } from '../types/roles';
import { AuditService } from '../services/firebase/AuditService';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000; // Show warning 2 minutes before timeout
const CHECK_INTERVAL_MS = 10 * 1000;     // Check every 10 seconds

/**
 * Auth Context Interface
 */
interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  isProvider: boolean;
  isClient: boolean;
  roles: UserRole[];
  primaryRole: UserRole | null;
  hasRole: (role: UserRole) => boolean;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider Component
 * Wraps the app and provides auth state + session timeout to all components.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Session timeout state
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutSecondsLeft, setTimeoutSecondsLeft] = useState(120);
  const lastActivityRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Fetch user profile from Firestore
   */
  const fetchUserProfile = async (uid: string) => {
    try {
      // Race against a timeout to prevent infinite spinner if Firestore hangs
      const profile = await Promise.race([
        firebaseAuthService.getUserProfile(uid),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 10000))
      ]);
      setUserProfile(profile);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile(null);
    }
  };

  const refreshProfile = async () => {
    // Use auth.currentUser as fallback for stale closure during registration
    const currentUser = user || auth.currentUser;
    if (currentUser) {
      setUser(currentUser);
      await fetchUserProfile(currentUser.uid);
    }
  };

  /**
   * Logout user and clean up timeout state
   */
  const logout = useCallback(async () => {
    try {
      setShowTimeoutWarning(false);
      if (countdownRef.current) clearInterval(countdownRef.current);
      await firebaseAuthService.logout();
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  }, []);

  /**
   * Reset the idle timer — called on user activity
   */
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showTimeoutWarning) {
      setShowTimeoutWarning(false);
      if (countdownRef.current) clearInterval(countdownRef.current);
    }
  }, [showTimeoutWarning]);

  /**
   * Handle automatic session timeout — log audit event then logout
   */
  const handleSessionTimeout = useCallback(async () => {
    try {
      const uid = user?.uid || auth.currentUser?.uid;
      if (uid) {
        const role = userProfile?.roles?.includes('client') || userProfile?.role === 'client' ? 'client' : 'provider';
        await AuditService.logAuthEvent(uid, 'logout', role);
      }
    } catch {
      // Best-effort audit logging — don't block logout
    }
    logout();
  }, [user, userProfile, logout]);

  /**
   * Handle "Stay Logged In" from the timeout warning
   */
  const handleStayLoggedIn = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  /**
   * Listen to auth state changes
   */
  useEffect(() => {
    const unsubscribe = firebaseAuthService.onAuthStateChanged(async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        await fetchUserProfile(firebaseUser.uid);
        lastActivityRef.current = Date.now();
      } else {
        setUserProfile(null);
        setShowTimeoutWarning(false);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Track user activity (mouse, keyboard, touch, scroll)
   */
  useEffect(() => {
    if (!user) return;

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [user]);

  /**
   * Check for idle timeout periodically
   */
  useEffect(() => {
    if (!user) {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      return;
    }

    checkIntervalRef.current = setInterval(() => {
      const idleTime = Date.now() - lastActivityRef.current;

      if (idleTime >= IDLE_TIMEOUT_MS) {
        // Session expired — audit log + force logout
        handleSessionTimeout();
      } else if (idleTime >= IDLE_TIMEOUT_MS - WARNING_BEFORE_MS && !showTimeoutWarning) {
        // Show warning
        const secondsLeft = Math.ceil((IDLE_TIMEOUT_MS - idleTime) / 1000);
        setTimeoutSecondsLeft(secondsLeft);
        setShowTimeoutWarning(true);

        // Start countdown
        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
          setTimeoutSecondsLeft((prev) => {
            if (prev <= 1) {
              handleSessionTimeout();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [user, showTimeoutWarning, handleSessionTimeout]);

  const isAuthenticated = user !== null;
  const PROVIDER_ROLES = ['provider', 'system_admin', 'clinical_director', 'admin_staff'];
  const isProvider = userProfile?.roles?.some((r: string) => PROVIDER_ROLES.includes(r)) || (!!userProfile?.role && PROVIDER_ROLES.includes(userProfile.role)) || false;
  const isClient = userProfile?.roles?.includes('client') || userProfile?.role === 'client' || false;

  // Multi-role support: expose roles array, primaryRole, and hasRole helper
  const roles: UserRole[] = useMemo(() => {
    if (userProfile?.roles) return userProfile.roles;
    if (userProfile?.role) return [userProfile.role as UserRole];
    return [];
  }, [userProfile]);

  const primaryRole: UserRole | null = userProfile?.primaryRole || (roles.length > 0 ? roles[0] : null);

  const hasRole = useCallback((role: UserRole): boolean => {
    return roles.includes(role);
  }, [roles]);

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    isAuthenticated,
    isProvider,
    isClient,
    roles,
    primaryRole,
    hasRole,
    logout,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showTimeoutWarning && user && (
        <SessionTimeoutWarning
          secondsRemaining={timeoutSecondsLeft}
          onStayLoggedIn={handleStayLoggedIn}
          onLogout={logout}
        />
      )}
    </AuthContext.Provider>
  );
};

/**
 * useAuth Hook
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
